import { Box, Group, Modal, Stack, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCamera, IconCheck, IconPhotoUp, IconRefresh } from '@tabler/icons-react';
import { prepareZXingModule, Scanner, IDetectedBarcode, IScannerError } from '@yudiel/react-qr-scanner';
import { DeviceQrPairResponse } from 'api/actions/device-qr-pair/device-qr-pair-response';
import { useApi } from 'api/api-context';
import { BarcodeDetector } from 'barcode-detector';
import Button from 'components/Button';
import UploadFile from 'components/files/UploadFile';
import Toast from 'components/Toast';
import P2Regular from 'components/typography/P2Regular';
import { parseDeviceQrUrl } from 'features/device-qr/parse-device-qr';
import { useCallback, useState } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

const zxingModuleOptions = {
  overrides: {
    locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? '/zxing_reader.wasm' : prefix + path),
  },
};

prepareZXingModule(zxingModuleOptions);
const qrImageDetector = new BarcodeDetector({ formats: ['qr_code'] });

type ScannerSource = 'camera' | 'image';

interface DeviceQrPairingModalBaseProps {
  deviceName: string;
  opened: boolean;
  onClose: () => void;
}

export interface DeviceQrPairingProps extends DeviceQrPairingModalBaseProps {
  mode: 'pair';
  deviceId: number;
  onPaired: (response: DeviceQrPairResponse) => void;
}

export interface DeviceQrSelectionProps extends DeviceQrPairingModalBaseProps {
  mode: 'select';
  onSelected: (qrId: string) => void;
}

export type DeviceQrPairingModalProps = DeviceQrPairingProps | DeviceQrSelectionProps;

/** Maps scanner startup failures to localized guidance. */
function scannerErrorMessage(error: IScannerError) {
  if (error.kind === 'permission-denied' || error.kind === 'security') {
    return 'Prístup ku kamere bol zamietnutý. Povoľte kameru v nastaveniach prehliadača a skúste to znova.';
  }
  if (error.kind === 'no-camera') {
    return 'Na tomto zariadení sa nenašla žiadna kamera.';
  }
  return 'Kameru sa nepodarilo spustiť. Skontrolujte jej dostupnosť a skúste to znova.';
}

/** Scans, checks, and confirms one device QR pairing. */
export default function DeviceQrPairingModal(props: DeviceQrPairingModalProps) {
  const { mode, deviceName, opened, onClose } = props;
  const { getAction } = useApi();
  const [qrId, setQrId] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [paused, setPaused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readingImage, setReadingImage] = useState(false);
  const [source, setSource] = useState<ScannerSource>('camera');
  const [scannerKey, setScannerKey] = useState(0);
  const [scannerError, setScannerError] = useState<IScannerError>();
  const checkQr = getAction('DeviceQrCheck');
  const pairQr = getAction('DeviceQrPair');

  /** Validates and checks one decoded QR value before enabling confirmation. */
  const checkDecodedQr = useCallback(
    async (rawValue: string) => {
      setMessage(undefined);
      setScannerError(undefined);
      const parsedQrId = parseDeviceQrUrl(rawValue);

      if (!parsedQrId) {
        setMessage('QR kód nemá platnú adresu pre túto aplikáciu. Naskenujte správny QR kód.');
        setPaused(false);
        return;
      }

      if (mode === 'select') {
        setQrId(parsedQrId);
        return;
      }

      try {
        const response = await checkQr({
          parameters: { deviceId: String(props.deviceId) },
          query: { qrId: parsedQrId },
        });
        setQrId(response.qrId);
      } catch (error: any) {
        if (error?.response?.status === 409 || error?.response?.data?.error?.code === 4093101) {
          setMessage('Tento QR kód je už spárovaný s iným zariadením.');
        } else {
          setMessage('QR kód sa nepodarilo overiť. Skúste ho naskenovať znova.');
        }
        setPaused(false);
      }
    },
    [checkQr, mode, props]
  );

  /** Checks the first detected camera QR code before enabling confirmation. */
  const handleScan = useCallback(
    async (codes: IDetectedBarcode[]) => {
      if (paused || !codes[0]) {
        return;
      }

      setPaused(true);
      await checkDecodedQr(codes[0].rawValue);
    },
    [checkDecodedQr, paused]
  );

  /** Decodes the first QR code in an uploaded image and checks its value. */
  const handleImage = useCallback(
    async (file?: File) => {
      if (!file) {
        return;
      }

      setReadingImage(true);
      setMessage(undefined);
      setQrId(undefined);
      setScannerError(undefined);

      try {
        const results = await qrImageDetector.detect(file);
        const rawValue = results[0]?.rawValue;

        if (!rawValue) {
          setMessage('Na vybranom obrázku sa nepodarilo nájsť čitateľný QR kód. Vyberte iný obrázok.');
          return;
        }

        await checkDecodedQr(rawValue);
      } catch {
        setMessage('Obrázok sa nepodarilo spracovať. Vyberte iný obrázok.');
      } finally {
        setReadingImage(false);
      }
    },
    [checkDecodedQr]
  );

  /** Presents a localized camera error and stops scanning. */
  const handleScannerError = useCallback((error: IScannerError) => {
    setScannerError(error);
    setMessage(scannerErrorMessage(error));
    setPaused(true);
  }, []);

  /** Restarts camera initialization after an error. */
  const retry = useCallback(() => {
    setMessage(undefined);
    setQrId(undefined);
    setScannerError(undefined);
    setPaused(false);
    setScannerKey((key) => key + 1);
  }, []);

  /** Stops the camera and displays the secondary image-upload option. */
  const useImage = useCallback(() => {
    setMessage(undefined);
    setQrId(undefined);
    setScannerError(undefined);
    setPaused(true);
    setSource('image');
  }, []);

  /** Returns to the default camera scanner. */
  const useCamera = useCallback(() => {
    setMessage(undefined);
    setQrId(undefined);
    setScannerError(undefined);
    setPaused(false);
    setSource('camera');
    setScannerKey((key) => key + 1);
  }, []);

  /** Returns the decoded ID to a form or persists it for an existing device. */
  const confirm = useCallback(async () => {
    if (!qrId) {
      return;
    }

    if (mode === 'select') {
      props.onSelected(qrId);
      onClose();
      return;
    }

    try {
      setSaving(true);
      const response = await pairQr({ parameters: { deviceId: String(props.deviceId) }, payload: { qrId } });
      props.onPaired(response);
      showNotification({
        title: 'QR kód bol spárovaný',
        message: `QR kód zariadenia ${deviceName} bol úspešne uložený.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      onClose();
    } catch (error: any) {
      setQrId(undefined);
      setPaused(false);
      setMessage(
        error?.response?.status === 409 || error?.response?.data?.error?.code === 4093101
          ? 'Tento QR kód medzitým spároval iný používateľ. Naskenujte iný QR kód.'
          : 'QR kód sa nepodarilo uložiť. Skúste ho naskenovať znova.'
      );
    } finally {
      setSaving(false);
    }
  }, [deviceName, mode, onClose, pairQr, props, qrId]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      title={mode === 'pair' ? `Párovanie QR kódu: ${deviceName}` : `Načítanie QR ID: ${deviceName}`}
      closeOnClickOutside={!saving}
      closeOnEscape={!saving}
      closeButtonProps={{ loading: saving }}
      size={540}
    >
      <Stack spacing={20}>
        <P2Regular>
          {source === 'camera'
            ? 'Naskenujte QR kód zadnou kamerou zariadenia.'
            : 'Vyberte obrázok, na ktorom je dobre viditeľný celý QR kód.'}
        </P2Regular>
        {!qrId && source === 'camera' && (
          <Box style={{ overflow: 'hidden', borderRadius: 8 }}>
            <Scanner
              key={scannerKey}
              formats={['qr_code']}
              constraints={{
                facingMode: { ideal: 'environment' },
                width: { ideal: 720 },
                height: { ideal: 720 },
              }}
              paused={paused}
              onScan={handleScan}
              onError={handleScannerError}
              sound={false}
              components={{ finder: true, onOff: false, torch: true, zoom: true }}
            />
          </Box>
        )}
        {!qrId && source === 'image' && (
          <UploadFile
            label="Obrázok s QR kódom"
            accept="image/*"
            description={readingImage ? 'Spracúvam obrázok…' : undefined}
            onFileProcessed={handleImage}
          />
        )}
        {message && (
          <Box
            data-testid="device-qr-scanner-error"
            data-device-qr-scanner-error-kind={scannerError?.kind}
            data-device-qr-scanner-error-message={scannerError?.message}
          >
            <Toast fullWidth type="fail" withCloseButton={false} message={message} />
          </Box>
        )}
        {qrId && <TextInput label="QR ID" value={qrId} readOnly data-testid="device-qr-id" />}
        <Group position="apart" spacing={16} data-testid="device-qr-pairing-actions">
          <Box>
            {!qrId &&
              (source === 'camera' ? (
                <Button
                  variant="subtle-gray"
                  onClick={useImage}
                  disabled={saving}
                  leftIcon={<IconPhotoUp stroke={1.5} size={24} />}
                  data-autofocus
                >
                  Nahrať obrázok s QR kódom
                </Button>
              ) : (
                <Button
                  variant="subtle-gray"
                  onClick={useCamera}
                  disabled={readingImage || saving}
                  leftIcon={<IconCamera stroke={1.5} size={24} />}
                >
                  Použiť kameru
                </Button>
              ))}
          </Box>
          <Group spacing={16}>
            {message && !qrId && source === 'camera' && (
              <Button
                variant="secondary"
                onClick={retry}
                disabled={saving}
                leftIcon={<IconRefresh stroke={1.5} size={24} />}
              >
                Skúsiť znova
              </Button>
            )}
            <Button
              variant="primary"
              onClick={confirm}
              disabled={!qrId || readingImage}
              loading={saving}
              leftIcon={<IconCheck stroke={1.5} size={24} />}
            >
              {mode === 'pair' ? 'Potvrdiť párovanie' : 'Použiť QR ID'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
